import {
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import {
  contarVentasEnVentana,
  nivelDesdeVentasEnVentana,
} from '../domain/vendedorNivelRules.js';

const CHARSET_CODIGO_VECINO = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
/** Código vecino único: 4 caracteres determinísticos desde el UID (sin query en `usuarios`). */
const USER_TAG_DETERMINISTIC_LEN = 4;

async function codigoVecinoDeterministicoDesdeUid(uid) {
  const enc = new TextEncoder().encode(`mlv|${uid}|user_tag`);
  const digest = await crypto.subtle.digest('SHA-256', enc);
  const arr = new Uint8Array(digest);
  let s = '';
  for (let i = 0; i < USER_TAG_DETERMINISTIC_LEN; i++) {
    const b = arr[i] ^ arr[i + 8] ^ arr[i + 16];
    s += CHARSET_CODIGO_VECINO[b % CHARSET_CODIGO_VECINO.length];
  }
  return s;
}

function normalizarCodigoVecinoInput(raw) {
  return String(raw || '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/[01IO]/g, '');
}

export function createProfileRepository({ getFirebase }) {
  function computarResumenNivel(perfil) {
    const u = perfil || {};
    const ventasTs = u.ventas_ts_window_ms || [];
    const ahora = Date.now();
    const ventasEnVentana = contarVentasEnVentana(ventasTs, ahora);
    let enTrial = false;
    const th = u.trial_nivel5_hasta;
    if (th?.toMillis) enTrial = ahora < th.toMillis();
    const nivelEfectivo = nivelDesdeVentasEnVentana(ventasEnVentana);
    return {
      ventasEnVentana,
      nivelEfectivo,
      enTrialNivel5: enTrial,
      premioNivel5Etiqueta: Boolean(u.premio_nivel5_etiqueta),
      deudaArs: Number(u.comision_deuda_ars) || 0,
      saldoFavorArs: Number(u.saldo_favor_ars) || 0,
      impagas: Number(u.comision_ventas_impagas_count) || 0,
      totalAppArs: Number(u.comision_total_app_ars) || 0,
    };
  }

  async function aplicarPostTrialEtiqueta(uid) {
    const { db } = getFirebase();
    const ref = doc(db, 'usuarios', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const d = snap.data() || {};
    const t = d.trial_nivel5_hasta;
    if (!t?.toMillis) return;
    if (Date.now() > t.toMillis() && !d.premio_nivel5_etiqueta) {
      await updateDoc(ref, {
        premio_nivel5_etiqueta: true,
        actualizadoEn: serverTimestamp(),
      });
    }
  }

  async function asegurarUserTagComprador(uid) {
    const { db } = getFirebase();
    const refDoc = doc(db, 'usuarios', uid);
    const snap = await getDoc(refDoc);
    if (!snap.exists()) return;
    const tag = await codigoVecinoDeterministicoDesdeUid(uid);
    const existingNorm = normalizarCodigoVecinoInput(snap.data()?.user_tag);
    if (existingNorm === tag) return;
    await setDoc(refDoc, { user_tag: tag, actualizadoEn: serverTimestamp() }, { merge: true });
  }

  function nombrePreLlenadoDesdeAuth() {
    const { auth } = getFirebase();
    const u = auth?.currentUser;
    return String(u?.displayName || '').trim() || null;
  }

  async function syncUserProfile(uid, email) {
    const { db } = getFirebase();
    const refDoc = doc(db, 'usuarios', uid);
    const snap = await getDoc(refDoc);
    if (!snap.exists()) {
      await setDoc(refDoc, {
        rol: 'vendedor',
        plan: 'free',
        negocioPremium: false,
        perfilCompleto: false,
        email: email || null,
        nombre: nombrePreLlenadoDesdeAuth(),
        habilitadoParaMarket: true,
        creadoEn: serverTimestamp(),
        ventas_ts_window_ms: [],
        comision_deuda_ars: 0,
        comision_ventas_impagas_count: 0,
        comision_total_app_ars: 0,
      });
      await asegurarUserTagComprador(uid);
      const snapN = await getDoc(refDoc);
      return { id: snapN.id, ...snapN.data() };
    }
    await aplicarPostTrialEtiqueta(uid);
    await asegurarUserTagComprador(uid);
    const snap2 = await getDoc(refDoc);
    return { id: snap2.id, ...snap2.data() };
  }

  async function loadUserProfile(uid, opts = {}) {
    const { db } = getFirebase();
    await aplicarPostTrialEtiqueta(uid);
    const refDoc = doc(db, 'usuarios', uid);
    const snap = opts.forceServer
      ? await getDocFromServer(refDoc)
      : await getDoc(refDoc);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  function firestoreStrOrNull(v) {
    if (v === undefined || v === null) return null;
    const s = String(v).trim();
    return s || null;
  }

  function sinUndefined(obj) {
    const out = {};
    for (const k of Object.keys(obj || {})) {
      const v = obj[k];
      if (v !== undefined) out[k] = v;
    }
    return out;
  }

  /**
   * Persiste datos del vendedor (nombre + teléfono + dirección) y, si quedan completos,
   * marca `perfilCompleto: true`. Sin DNI, sin OCR, sin colección `personas`.
   */
  async function guardarDatosVendedor(uid, payload) {
    const { db } = getFirebase();
    const data = sinUndefined({
      rol: 'vendedor',
      nombre: firestoreStrOrNull(payload?.nombre),
      telefono: firestoreStrOrNull(payload?.telefono),
      direccion: firestoreStrOrNull(payload?.direccion),
      calle: firestoreStrOrNull(payload?.calle),
      nro_casa: firestoreStrOrNull(payload?.nro_casa),
      foto_url: payload?.foto_url ?? undefined,
      perfilCompleto: payload?.perfilCompleto === true ? true : undefined,
      actualizadoEn: serverTimestamp(),
    });
    await setDoc(doc(db, 'usuarios', uid), data, { merge: true });
  }

  return {
    syncUserProfile,
    loadUserProfile,
    guardarDatosVendedor,
    resumenNivelVendedor: (perfil) => computarResumenNivel(perfil),
    computarResumenNivel,
  };
}
